import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useAppNode } from '@backstage/frontend-plugin-api';
import type { FrontendPluginInfo } from '@backstage/frontend-plugin-api';

export type PluginMetadata = FrontendPluginInfo;

const UNSET = Symbol('unset');

const PluginMetadataContext = createContext<
  PluginMetadata | undefined | typeof UNSET
>(UNSET);

export const PluginMetadataProvider = ({
  value,
  children,
}: {
  value?: PluginMetadata;
  children: ReactNode;
}) => createElement(PluginMetadataContext.Provider, { value }, children);

// `info()` is async and only resolves on the plugin instance the app installed,
// so it can't be read straight off the node during render.
export const usePluginMetadata = (): PluginMetadata | undefined => {
  const override = useContext(PluginMetadataContext);
  const plugin = useAppNode()?.spec.plugin;
  const [info, setInfo] = useState<FrontendPluginInfo>();

  useEffect(() => {
    if (override !== UNSET) {
      return undefined;
    }

    let active = true;
    plugin?.info().then(resolved => {
      if (active) {
        setInfo(resolved);
      }
    });
    return () => {
      active = false;
    };
  }, [override, plugin]);

  if (override !== UNSET) {
    return override;
  }

  return info;
};
